import { Button, Col, Row, Tooltip } from "antd";
import "ckeditor5/ckeditor5-content.css";
import "ckeditor5/ckeditor5.css";
import { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import {
  IoIosEyeOff,
  IoMdArrowDropdown,
  IoMdArrowDropright,
  IoMdClose,
  IoMdDownload,
} from "react-icons/io";
import { MdFilePresent } from "react-icons/md";
import apiFactory from "../../../api";
import { CustomAvatar } from "../../../components/avatar/CustomAvatar";

import { ForwardMailModal } from "../../../components/modal/ForwardMailModal";
import { useMenuContext } from "../../../context/MenuContext";
import { useInfoUser } from "../../../store/UserStore";
import { formatTime } from "../../../utils/formatTime";
import { uniqBy } from "lodash";
import { RESOURCE } from "../../../api/apiConstant";
import { useGlobalDownloader } from "../../../context/global/DownloadProvider";
import MailStorageFile from "../../../components/preview/MailStorageFile";
import { useAuthLayoutContext } from "../../../context/AuthLayoutContext";
import { useMailContextV2 } from "../../../context/MailContextV2";
import {
  blobToBase64,
  convertOembedToIframe,
  convertToUrl,
  openNotificationError,
} from "../../../utils/Utils";
import "./style.scss";

const ShowFile = ({ notification }) => {
  const createdAt = notification?.createdAt;
  const resources = notification?.resources || notification?.files || [];
  const [pickedList, setPickedList] = useState([]);

  const { startDownload } = useGlobalDownloader();

  const pickFile = (resourceId) => {
    if (!resourceId) return;

    if (pickedList.includes(resourceId)) {
      setPickedList(pickedList.filter((id) => id !== resourceId));
    } else {
      setPickedList([...pickedList, resourceId]);
    }
  };

  const handleDownload = async (e) => {
    e.stopPropagation();

    const result = await apiFactory.resourceApi.prepareZip(pickedList);

    if (result?.status !== 200) {
      openNotificationError("Prepare files for download failed");
    }

    startDownload(result?.data, "willo.zip");
  };

  return (
    <div className="w-full">
      {pickedList?.length > 0 && (
        <div className="inline-flex items-center gap-3 ml-0 sm:ml-[12px] mb-2 bg-[#e8f0fe] text-[#1967d2] px-2.5 py-1 rounded border border-[#d2e3fc] shadow-sm text-xs font-medium">
          <button
            onClick={() => setPickedList([])}
            className="hover:bg-[#d2e3fc] p-1 rounded transition cursor-pointer"
            title="Clear selection"
          >
            <IoMdClose className="text-[16px] text-[#5f6368]" />
          </button>
          <span>{pickedList?.length} selected</span>
          <button
            onClick={handleDownload}
            className="hover:bg-[#d2e3fc] px-2 py-0.5 rounded transition flex items-center gap-1.5 text-[#1a73e8] cursor-pointer"
            title="Download selected"
          >
            <IoMdDownload className="text-[18px]" />
            <span className="font-semibold">Download ZIP</span>
          </button>
        </div>
      )}
      {resources?.length > 0 && (
        <div className="ml-0 sm:mx-[12px] my-2">
          <div
            className="grid gap-3 w-full"
            style={{
              gridTemplateColumns:
                "repeat(auto-fill, minmax(max(160px, calc((100% - 36px) / 4)), 1fr))",
            }}
          >
            {resources?.map((item) => {
              const fileWithTimestamp = {
                ...item,
                createdAt,
              };

              return (
                <div
                  className="w-full min-w-0"
                  key={item?.resourceId}
                >
                  <MailStorageFile
                    file={fileWithTimestamp}
                    pickFile={pickFile}
                    pickedList={pickedList}
                    setPickedList={setPickedList}
                    key={item?.resourceId}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const RecipientCount = ({
  notification,
  setIsModalMembersReadUnread,
  setSelectedMessage,
}) => {
  const { orgList } = useAuthLayoutContext();

  const recipientRepresentatives = useMemo(() => {
    const filtered = orgList?.filter((user) =>
      notification?.recipientRepresentatives?.includes(user.userCode),
    );

    return uniqBy(filtered, "userId").map((user) => user?.name);
  }, [orgList, notification?.recipientRepresentatives]);

  const ccRepresentatives = useMemo(() => {
    const filtered = orgList?.filter((user) =>
      notification?.ccRepresentatives?.includes(user.userCode),
    );

    return uniqBy(filtered, "userId").map((user) => user?.name);
  }, [orgList, notification?.ccRepresentatives]);

  const formatCountText = (label, representatives = [], total = 0) => {
    if (total <= 0) return null;

    const names = representatives.join(", ");
    const remaining = total - representatives.length;

    if (!names) return `${label}: ${total}`;
    if (remaining <= 0) return `${label}: ${names}`;

    return `${label}: ${names} +${remaining}`;
  };

  return (
    <button
      className="text-[#5e5e5e] text-[12px] text-left"
      onClick={(e) => {
        e.stopPropagation();
        setIsModalMembersReadUnread(true);
        setSelectedMessage(notification);
      }}
    >
      {formatCountText(
        "Recipient",
        recipientRepresentatives,
        notification?.recipientCount,
      ) && (
          <div>
            {formatCountText(
              "Recipient",
              recipientRepresentatives,
              notification?.recipientCount,
            )}
          </div>
        )}

      {formatCountText(
        "Reference",
        ccRepresentatives,
        notification?.ccCount,
      ) && (
          <div>
            {formatCountText(
              "Reference",
              ccRepresentatives,
              notification?.ccCount,
            )}
          </div>
        )}
    </button>
  );
};

const SenderName = ({ notification }) => {
  const { orgList } = useAuthLayoutContext();

  const sender = orgList?.find(
    (org) => org?.userCode === notification?.senderId,
  );

  if (!sender)
    return <div className="font-bold">{notification?.senderName}</div>;

  return <div className="font-bold">{sender?.name}</div>;
};

const MailReplyV2 = forwardRef(
  ({
    notification,
    isLastItem,
    addMemberOrg,
    setSelectedMessage,
    setIsModalMembersReadUnread,
  }) => {
    const { selectedConversation } = useMenuContext();

    const { orgList } = useAuthLayoutContext();

    const { languageMap } = useInfoUser();
    const [isForwardMail, setIsForwardMail] = useState(false);
    const [processedContent, setProcessedContent] = useState("");
    const [previewFiles, setPreviewFiles] = useState([]);
    const [isOpenOut, setIsOpenOut] = useState(true);

    const changeOpenUp = () => {
      setIsOpenOut(!isOpenOut);
    };

    const isBase64 = (str) => {
      return str.startsWith("data:image/") && str.includes(";base64");
    };

    const getSender = () => {
      const sender = orgList?.find(
        (org) => org?.userCode === notification?.senderId,
      );
      return {
        ...sender,
        name: sender?.name ? sender?.name : notification?.senderName,
        userId: sender?.userCode ? sender?.userCode : notification?.senderId,
        position: sender?.userPosition,
      };
    };

    const getIconNotification = (notification) => {
      if (!notification?.files?.length) return;

      return (
        <div className="text-[#5e5e5e] justify-end items-end">
          <MdFilePresent size={20} color={"#3269c9"} />
        </div>
      );
    };

    const GetIconUnread = ({ notification, onClick }) => {
      if (notification?.oldConversationId) return null;

      return (
        <Button
          className="bg-[#2a56b9] text-white h-[20px] gap-[4px] zoom-09 flex items-center"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onClick?.(e);
          }}
        >
          <IoIosEyeOff size={12} />
          <span className="text-[12px]">{notification?.unreadCount}</span>
        </Button>
      );
    };

    const showOpenDownArrow = useMemo(() => {
      return (
        <div
          onClick={changeOpenUp}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          className="cursor-pointer"
        >
          {isOpenOut ? (
            <Tooltip
              placement="top"
              title={languageMap?.["modal.message.showLess"] ?? "show less"}
              color={"#0091ff"}
            >
              <IoMdArrowDropdown size={20} color="black" />
            </Tooltip>
          ) : (
            <Tooltip
              placement="top"
              title={languageMap?.["modal.message.showMore"] ?? "show more"}
              color={"#0091ff"}
            >
              <IoMdArrowDropright size={20} color="black" />
            </Tooltip>
          )}
        </div>
      );
    }, [isOpenOut]);

    const convertedContent = useMemo(() => {
      let html = processedContent;

      html = convertOembedToIframe(html);
      html = convertToUrl(html);

      return html;
    }, [processedContent]);

    const cancelForward = () => {
      setIsForwardMail(false);
    };

    useEffect(() => {
      const processReceivedImages = async () => {
        let content = notification?.content;
        if (!content) {
          setProcessedContent("");
          return;
        }

        const doc = new DOMParser().parseFromString(content, "text/html");
        const images = Array.from(doc.querySelectorAll("img"));

        const imagePromises = images.map(async (imgDoc) => {
          if (!imgDoc?.src) return Promise.resolve();

          if (isBase64(imgDoc.src)) return Promise.resolve();

          if (!imgDoc.src.includes(RESOURCE)) {
            return Promise.resolve();
          }

          const parts = imgDoc.src.split("/");
          const resourceId = parts.pop();
          if (!resourceId || resourceId === "null") {
            return Promise.resolve();
          }

          try {
            const fetchedBlob =
              await apiFactory.resourceApi.getStreamFileAsBlob(resourceId);

            if (
              !fetchedBlob ||
              fetchedBlob.size === 0 ||
              fetchedBlob.type.startsWith("application/json")
            ) {
              imgDoc.remove();
              return;
            }

            const base64Data = await blobToBase64(fetchedBlob);

            imgDoc.src = base64Data;
          } catch (error) {
            console.error("Failed to load received image:", resourceId, error);
            imgDoc.remove();
          }
        });

        await Promise.all(imagePromises);

        setProcessedContent(doc.body.innerHTML);
      };

      processReceivedImages();
    }, [notification?.content]);

    useEffect(() => {
      if (notification?.draftMail?.files) {
        setPreviewFiles((prev) => {
          const oldIds = prev?.map((f) => f?.resourceId) || [];
          const newFiles = notification?.draftMail?.files?.filter(
            (f) => !oldIds?.includes(f?.resourceId),
          );
          return [
            ...prev,
            ...newFiles.map((f) => ({ ...f, fileDraftOld: true })),
          ];
        });
      }
    }, [notification?.draftMail?.files]);

    useEffect(() => {
      if (convertedContent) {
        setIsOpenOut(isLastItem ?? false);
      }
    }, [convertedContent]);

    return (
      <div>
        {!notification?.isDraft && (
          <div key={notification?.notificationId}>
            <div
              className="flex justify-between cursor-pointer"
              onClick={changeOpenUp}
              onMouseDown={(e) => {
                e.preventDefault();
              }}
            >
              <div className="flex items-start gap-3 mb-2 flex-1 min-w-0">
                <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0 pt-0.5">
                  <CustomAvatar
                    person={getSender()}
                    userId={getSender()?.userId}
                    styleCss={{}}
                    isShowDetail={true}
                    isShowUserStatus={{ status: true, isAlwaysTrue: false }}
                  />
                </div>

                <div className="mr-2 min-w-0 flex-1">
                  <div className="text-[14px] flex items-center">
                    <SenderName notification={notification} />
                    <div className="ml-2">{showOpenDownArrow}</div>
                  </div>
                  <div className="text-[#5e5e5e] text-[12px]">
                    <RecipientCount
                      notification={notification}
                      setIsModalMembersReadUnread={setIsModalMembersReadUnread}
                      setSelectedMessage={setSelectedMessage}
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-[10px] justify-center flex-shrink-0 ml-2">
                {getIconNotification(notification)}
                {selectedConversation?.unreadCount > 0 &&
                  (!selectedConversation?.lastSeenTs ||
                    selectedConversation?.lastSeenTs <
                    notification?.createdAt) && (
                    <div className="bg-[#188038] text-white h-[20px] gap-[4px] zoom-09 flex items-center justify-center rounded-full px-[8px]">
                      <span className="text-[12px]">new</span>
                    </div>
                  )}
                {!notification?.oldConversationId && (
                  <GetIconUnread
                    notification={notification}
                    onClick={() => {
                      setIsModalMembersReadUnread(true);
                      setSelectedMessage(notification);
                    }}
                  />
                )}
                {notification?.resources?.length > 0 && (
                  <MdFilePresent size={20} color={"#3269c9"} />
                )}
                <div className="text-[#5e5e5e]">
                  {formatTime(notification?.createdAt)}
                </div>
              </div>
            </div>
            <div className="ml-0 sm:ml-[12px] text-[15px] break-words custom-collapsible pb-[6px]">
              <div
                className="ck-content !h-auto"
                dangerouslySetInnerHTML={{
                  __html: convertedContent,
                }}
              />
            </div>
            <div
              style={{ display: isOpenOut && notification ? "block" : "none" }}
            >
              <ShowFile notification={notification} />
            </div>
          </div>
        )}

        {isForwardMail && (
          <ForwardMailModal
            notification={notification}
            isForwardMail={isForwardMail}
            setIsForwardMail={setIsForwardMail}
            addMemberOrg={addMemberOrg}
            cancelForward={cancelForward}
          />
        )}
      </div>
    );
  },
);
export { MailReplyV2 };
